import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { CustomGraphComponent } from '../../../../components/custom-graph/custom-graph.component';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { BmiDetailsService } from '../../services/bmi-details.service';
import { BMIHistoryResponse } from '../../bmi-const/bmi-details-models';
import { TimePeriodFilter } from '../../../assesment-detail/assesment-const/assessment-details-models';

@Component({
  selector: 'patient-bmi-graph',
  standalone: true,
  imports: [CommonModule, DropdownModule, FormsModule, CustomGraphComponent],
  templateUrl: './bmi-graph.component.html',
  styleUrl: './bmi-graph.component.css'
})
export class BmiGraphComponent implements OnInit {
  private authService = inject(AuthService);
  private bmiDetailsService = inject(BmiDetailsService);

  patientId!: string;
  loading: boolean = true;
  bmiHistoryData: BMIHistoryResponse | null = null;

  // Graph Data Bindings
  graphDates: string[] = [];
  graphValues: number[] = [];
  latestProgress: number = 0;
  isPositive: boolean = true;

  // Filter Configuration
  filterOptions = [
    { label: 'Weekly', value: TimePeriodFilter.Weekly },
    { label: 'Monthly', value: TimePeriodFilter.Monthly },
    { label: 'Yearly', value: TimePeriodFilter.Yearly }
  ];
  selectedFilter: TimePeriodFilter = TimePeriodFilter.Monthly;

  ngOnInit() {
    this.authService.getUserProfile().subscribe((res) => {
      if (res?.PatientId) {
        this.patientId = res.PatientId;
        this.fetchGraphData();
      }
    });
  }

  fetchGraphData() {
    this.loading = true;
    this.bmiDetailsService.getBMIHistory(this.patientId, this.selectedFilter).subscribe({
      next: (response) => {
        if (response && response.status === 200) {
          this.bmiHistoryData = response;
          this.processGraphData();
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load BMI graph history:', err);
        this.loading = false;
      }
    });
  }

  onFilterChange() {
    this.fetchGraphData();
  }

  private processGraphData() {
    if (!this.bmiHistoryData || !this.bmiHistoryData.bmi_history) return;
    
    const sortedHistory = [...this.bmiHistoryData.bmi_history].reverse();
    
    this.graphDates = sortedHistory.map(item => this.formatDate(item.date));
    this.graphValues = sortedHistory.map(item => item.value);
    
    this.latestProgress = this.bmiHistoryData.progress_from_last_bmi;
    this.isPositive = this.bmiHistoryData.is_positive;
  }

  private formatDate(dateStr: string): string {
    if (!dateStr || dateStr.length < 10) return dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const day = parts[0];
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const monthIndex = parseInt(parts[1], 10) - 1;
      return `${day} ${monthNames[monthIndex]}`;
    }
    return dateStr;
  }

  getHintText(): string {
     if (this.latestProgress === 0) {
         return "Your BMI has remained stable. Maintaining balanced nutrition and regular activity can help you reach a healthy BMI range.";
     }
     if (this.isPositive) {
         return "Your BMI is trending upward. Maintaining balanced nutrition and regular activity can help you reach a healthy BMI range.";
     } else {
         return "Your BMI is trending downward. Maintaining balanced nutrition and regular activity can help you reach a healthy BMI range.";
     }
  }
}

